import type { LocationKind } from '../types';

interface PlaceArtworkProps {
  kind: LocationKind;
  emoji: string;
}

const locationArtworkUrls: Partial<Record<LocationKind, string>> = {
  room: '/assets/locations/room.webp',
  pool: '/assets/locations/pool.webp',
  beach: '/assets/locations/beach.webp',
  relax: '/assets/locations/relax.webp',
  restaurant: '/assets/locations/restaurant.webp',
  around: '/assets/locations/around.webp',
  gym: '/assets/locations/gym-v2.webp',
};

export function PlaceArtwork({ kind, emoji }: PlaceArtworkProps) {
  const artworkUrl = locationArtworkUrls[kind];

  if (artworkUrl) {
    return (
      <img
        className="place-artwork"
        src={artworkUrl}
        alt=""
        width="720"
        height="408"
        decoding="async"
        aria-hidden="true"
      />
    );
  }

  if (kind === 'custom') {
    return <div className="place-artwork place-artwork--custom" aria-hidden="true"><span>{emoji}</span></div>;
  }

  return (
    <svg className={`place-artwork place-artwork--${kind}`} viewBox="0 0 360 178" aria-hidden="true">
      <defs>
        <linearGradient id={`sky-${kind}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#dff7ff" />
          <stop offset="1" stopColor="#f7fbff" />
        </linearGradient>
        <linearGradient id={`water-${kind}`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#24c4eb" />
          <stop offset="1" stopColor="#4fd8ad" />
        </linearGradient>
      </defs>
      <rect width="360" height="178" rx="24" fill={`url(#sky-${kind})`} />
      {kind === 'room' && (
        <>
          <rect x="24" y="34" width="312" height="126" rx="18" fill="#eaf0fb" />
          <rect x="46" y="78" width="177" height="62" rx="13" fill="#fff" stroke="#afc4e8" strokeWidth="4" />
          <rect x="52" y="62" width="70" height="32" rx="12" fill="#c8d8f2" />
          <rect x="224" y="48" width="80" height="92" rx="10" fill="#223760" />
          <circle cx="287" cy="95" r="5" fill="#4de2ff" />
          <path d="M34 142h292" stroke="#0fb9e8" strokeWidth="7" strokeLinecap="round" opacity=".45" />
        </>
      )}
      {kind === 'pool' && (
        <>
          <circle cx="292" cy="42" r="24" fill="#ffd166" />
          <path d="M0 98c54-20 93 23 145 2s102 18 148 0 67-8 67-8v86H0z" fill={`url(#water-${kind})`} />
          <path d="M0 126c47-20 89 21 140 0s98 17 143 0 77-5 77-5" fill="none" stroke="#eaffff" strokeWidth="7" opacity=".8" />
          <path d="M56 82v58M56 96h44M100 82v58" fill="none" stroke="#fff" strokeWidth="9" strokeLinecap="round" />
          <rect x="226" y="71" width="74" height="36" rx="18" fill="#ff6b8a" transform="rotate(-8 226 71)" />
        </>
      )}
      {kind === 'beach' && (
        <>
          <circle cx="294" cy="40" r="24" fill="#ffd166" />
          <path d="M0 92c66-17 108 15 172 0s115 16 188-2v54H0z" fill="#35cae9" />
          <path d="M0 126c91-12 180 6 360-5v57H0z" fill="#f7d48a" />
          <path d="M75 103c16-52 91-52 107 0z" fill="#ff6b8a" />
          <path d="M128 55v105" stroke="#223760" strokeWidth="6" />
          <path d="M44 152h248" stroke="#d9ae59" strokeWidth="6" strokeLinecap="round" opacity=".7" />
        </>
      )}
      {kind === 'relax' && (
        <>
          <circle cx="64" cy="47" r="28" fill="#4fd8ad" opacity=".22" />
          <circle cx="300" cy="43" r="35" fill="#0fb9e8" opacity=".16" />
          <path d="M76 125c42-57 168-59 209 0" fill="#fff" stroke="#afc4e8" strokeWidth="5" />
          <rect x="93" y="104" width="172" height="43" rx="22" fill="#fff" />
          <rect x="103" y="93" width="58" height="38" rx="18" fill="#c8d8f2" />
          <path d="M58 151c33-20 63-18 94 0M211 151c32-21 61-19 91 0" fill="none" stroke="#4fd8ad" strokeWidth="9" strokeLinecap="round" />
          <path d="M180 35c-17 19 17 24 0 43M205 30c-17 19 17 24 0 43" fill="none" stroke="#0fb9e8" strokeWidth="6" strokeLinecap="round" opacity=".6" />
        </>
      )}
      {kind === 'restaurant' && (
        <>
          <circle cx="180" cy="91" r="58" fill="#fff" stroke="#afc4e8" strokeWidth="6" />
          <circle cx="180" cy="91" r="34" fill="#e7efff" />
          <path d="M85 39v104M68 39v45c0 18 34 18 34 0V39M276 39c-27 27-24 63 0 65v39" fill="none" stroke="#223760" strokeWidth="8" strokeLinecap="round" />
          <path d="M137 91h86" stroke="#0fb9e8" strokeWidth="7" strokeLinecap="round" opacity=".55" />
          <path d="M180 48v86" stroke="#4fd8ad" strokeWidth="7" strokeLinecap="round" opacity=".5" />
        </>
      )}
      {kind === 'around' && (
        <>
          <path d="M0 137c48-31 87-20 126-44s80-24 118 0 72 18 116-8v93H0z" fill="#dce8f8" />
          <path d="M180 39c-32 0-58 25-58 56 0 43 58 81 58 81s58-38 58-81c0-31-26-56-58-56z" fill={`url(#water-${kind})`} />
          <circle cx="180" cy="94" r="22" fill="#fff" />
          <path d="M20 150c55-29 107-14 148-39s91 1 172 25" fill="none" stroke="#fff" strokeWidth="7" strokeLinecap="round" strokeDasharray="10 12" />
        </>
      )}
    </svg>
  );
}
